import { useState, useEffect, forwardRef, useRef, useCallback } from "react";
import { useTheme } from "next-themes";
import { format4, numberWithCommas, separateNumber } from "../utils/numbers";
import { useI18n } from "../hooks/useI18n";
import { Link } from "react-router-dom";
import useWindowSize from "../hooks/useWindowSize";
import { getNavHistory } from "../services/funds.service";
import retryApi from "../utils/api";
import dayjs from "dayjs";


const FundItem = forwardRef(({ data, utils, toggleModal, showDetails, investAmount, t }, ref) => {
  const [navMonthlyHistory, setNavMonthlyHistory] = useState(null);
  const [navYearlyHistory, setNavYearlyHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const { isRTL } = useI18n();
  const screenSize = useWindowSize();
  const abortRef = useRef(null);

  const calculatedUnits = data?.navData.offer_price ? (investAmount ? format4(parseFloat(investAmount) / parseFloat(data?.navData.offer_price)) : "") : "0";
  // console.log(calculatedUnits, typeof calculatedUnits);
  let unitsWithComma = "";

  if (calculatedUnits !== "" && calculatedUnits !== "0") {
    const { whole, decimal } = utils.separate(String(calculatedUnits));

    unitsWithComma = numberWithCommas(whole) + "." + decimal;
  } else if (calculatedUnits === "0" && investAmount !== "") {
    unitsWithComma = "0.0000";
  } else if (investAmount === "") {
    unitsWithComma = "";
  }

  const { theme } = useTheme();

  const hasNavData = (data.navData && Object.keys(data.navData).length !== 0) ? true : false;

  // console.log(data.navData.offer_price);
  const navRounded = format4(hasNavData ? data.navData?.offer_price || 0 : data.nav);

  const nav = utils.separate(navRounded);

  const totalUnits = separateNumber(hasNavData ? data.navData?.total_fund_units : "0", { format: true, digits: 4 });

  // const x = ["23.4", "4.62", "53", "38.1", "12.56"]

  // const monthlyReturn = separateNumber(x[Math.floor(Math.random() * 4) + 1], { format: true, digits: 2 });
  // const yearlyReturn = separateNumber(x[Math.floor(Math.random() * 4) + 1], { format: true, digits: 2 });

  const cssRTLAdjust = isRTL ? {
    left: '1px',
    top: '-1px',
    right: ''
  } : {}

  const computeMonthlyReturn = useCallback(() => {

    if (!navMonthlyHistory || navMonthlyHistory?.data?.length < 2) return "0";

    let oldest = navMonthlyHistory.data[0];
    let newest = navMonthlyHistory.data[0];

    navMonthlyHistory.data.forEach((item) => {
      if (dayjs(item.nav_time).isBefore(oldest.nav_time)) {
        oldest = item;
      } 
      if (dayjs(item.nav_time).isAfter(newest.nav_time)) {
        newest = item;
      } 
    })

    if (oldest.nav !== 0) {
        return String(((newest.nav - oldest.nav) / oldest.nav) * 100)
    } else {
        return "0";
    }

  }, [navMonthlyHistory])

  const computeYearlyReturn = useCallback(() => {

    if (!navYearlyHistory || navYearlyHistory?.data?.length < 2) return "0";

    let oldest = navYearlyHistory.data[0];
    let newest = navYearlyHistory.data[0];

    navYearlyHistory.data.forEach((item) => {
      if (dayjs(item.nav_time).isBefore(oldest.nav_time)) {
        oldest = item;
      } 
      if (dayjs(item.nav_time).isAfter(newest.nav_time)) {
        newest = item;
      } 
    })

    if (oldest.nav !== 0) {
        return String(((newest.nav - oldest.nav) / oldest.nav) * 100)
    } else {
        return "0";
    }

  }, [navYearlyHistory])

  const monthlyReturn = separateNumber(computeMonthlyReturn(), { format: true, digits: 2 });
  const yearlyReturn = separateNumber(computeYearlyReturn(), { format: true, digits: 2 });

  useEffect(() => {

    const getFundsNavHistory = async (retries = 3) => {

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const ac = new AbortController();
      abortRef.current = ac;

      setLoading(true);

      const navDate = data?.navData?.nav_date; // e.g. "2025-08-10T00:00:00.000Z"

      // choose navDate if present and valid, otherwise use now
      let today = navDate ? dayjs(navDate) : dayjs();
      if (!today.isValid()) today = dayjs(); // fallback if input was invalid

      const currentDate = today.format('YYYY-MM-DD');
      const prev30Date = today.subtract(30, 'day').format('YYYY-MM-DD');
      const prevYearDate = today.subtract(1, 'year').format('YYYY-MM-DD');

      try {
        const [monthlyResponse, quarterlyResponse] = await Promise.all([
          retryApi((signal) => getNavHistory(data.symbol_code, prev30Date, currentDate, signal), retries, ac.signal),
          retryApi((signal) => getNavHistory(data.symbol_code, prevYearDate, currentDate, signal), retries, ac.signal),
        ])

        setNavMonthlyHistory(monthlyResponse?.data);
        setNavYearlyHistory(quarterlyResponse?.data);

        if (monthlyResponse.error && quarterlyResponse.error) {
          // console.log('err after retries', monthlyResponse, quarterlyResponse)
        } else {
          // console.log('no errors', monthlyResponse, quarterlyResponse)
        }

      } catch (err) {
        // console.log(err)
      } finally {
        setLoading(false);
      }
    }

    getFundsNavHistory();

  }, [data]);

  return (
    <div className='col-12 p-0 animate-card' ref={ref ?? ref} id={data.symbol_code}>
      <div className='row w-100 mx-auto align-items-center py-1 flex-nowrap gap-sm-1 gap-lg-4'>
        <div className='col col-sm-2 col-md-2 col-lg-2 col-xl-2 p-0 ps-1 pe-2 pe-sm-1'>
          <div
            // to={`/invest/${data.symbol_code}`}
            onClick={() => showDetails(() => ({
              show: true,
              symbol: data.symbol_code
            }))}
            className=""
            style={{
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <div
              className='text-truncate'
              style={{
                fontSize: screenSize >= 768 ? "1.5rem" : screenSize >= 576 && screenSize <= 767 ? "1.5rem" : "1.35rem",
                lineHeight: "1",
                fontWeight: "500",
                // display: '-webkit-box',
                // boxOrient: 'vertical',
                // WebkitBoxOrient: 'vertical',
                // WebkitLineClamp: '3',
                overflow: "hidden",
                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
              }}
            >
              {data.symbol_code}
            </div>
            <div
              className=''
              style={{
                fontSize: screenSize >= 576 ? "0.82rem" : ".77rem",
                fontWeight: "400",
                lineHeight: "1",
                color: theme === "dark" ? "rgba(255,255,255,.6)" : "var(--theme-light-secondary)",
                marginTop: "4px",
                display: "-webkit-box",
                boxOrient: "vertical",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "3",
                overflow: "hidden",
              }}
            >
              {data.fund_name}
            </div>
          </div>
        </div>
        <div className='col p-0'>
          <div className=''>
            <div
              className='text-right-fixed'
              style={{
                minWidth: screenSize >= 576 ? "76px" : "58px",
                // display: "inline-block",
              }}
            >
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "0.6rem" : "0.74rem",
                  fontSize: screenSize >= 992 ? "0.76rem" : screenSize <= 991 && screenSize >= 576 ? "0.76rem" : "0.72rem",
                  fontWeight: "400",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                  letterSpacing: ".05em",
                }}
              >
                {t("app.fund.price")}
              </div>
              <div
                className=''
                dir="ltr"
                style={{
                  // fontSize: screenSize <= 991 ? "1.1rem" : "1.14rem",
                  fontSize: screenSize >= 992 ? "1.3rem" : screenSize <= 991 && screenSize >= 576 ? "1.3rem" : "1.3rem",
                  fontWeight: "400",
                  lineHeight: "1.1",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                  marginTop: screenSize <= 991 ? "3px" : "2px",
                }}
              >
                <span
                dir="ltr"
                  style={{
                    fontSize: screenSize >= 992 ? "0.8rem" : screenSize <= 991 && screenSize >= 576 ? "0.8rem" : "0.75rem",
                    color: theme === "dark" ? "rgba(210, 124, 216, .86)" : "rgba(16, 109, 221, .76)",
                    marginRight: isRTL ? "6px" : '5px',
                  }}
                >
                  {t("app.fund.rupee")}
                </span>
                {nav.whole === "0" && nav.decimal === "00" ? "0" : !nav.decimal ? numberWithCommas(nav.whole) : numberWithCommas(nav.whole) + "."}
                <span
                  style={{
                    fontSize: screenSize >= 992 ? ".965rem" : screenSize <= 991 && screenSize >= 576 ? "0.965rem" : ".85rem",
                  }}
                >
                  {nav.whole === "0" && nav.decimal === "00" ? "" : !nav.decimal ? "" : nav.decimal}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* <div className='col p-0'>
            <div
              className='text-right-fixed'
              style={{
                minWidth: screenSize >= 576 ? "76px" : "58px",
                // display: "inline-block",
              }}
            >
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "0.6rem" : "0.74rem",
                  fontSize: screenSize >= 992 ? "0.76rem" : screenSize <= 991 && screenSize >= 576 ? "0.76rem" : "0.72rem",
                  fontWeight: "400",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                  letterSpacing: ".05em",
                }}
              >
                {t("app.fund.issued")}
              </div>
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "1.1rem" : "1.14rem",
                  fontSize: screenSize >= 992 ? "1.3rem" : screenSize <= 991 && screenSize >= 576 ? "1.3rem" : "1.3rem",
                  fontWeight: "400",
                  lineHeight: "1.1",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                  marginTop: screenSize <= 991 ? "3px" : "2px",
                }}
              >
                {numberWithCommas(totalUnits.whole) + (totalUnits.hasDecimal ? '.' : '')}
                {totalUnits.hasDecimal ? (
                <span
                  style={{
                    fontSize: screenSize >= 992 ? ".965rem" : screenSize <= 991 && screenSize >= 576 ? "0.965rem" : ".85rem",
                  }}
                >
                  {totalUnits.decimal}
                </span>) : ''}
                <span
                  style={{
                    fontSize: screenSize >= 992 ? "0.8rem" : screenSize <= 991 && screenSize >= 576 ? "0.8rem" : "0.75rem",
                    color: theme === "dark" ? "rgba(210, 124, 216, .86)" : "rgba(16, 109, 221, .76)",
                    marginLeft: '4px'
                  }}
                >
                  units
                </span>
              </div>
            </div>
        </div> */}
        <div className='col p-0'>
            <div
              className='text-right-fixed'
              style={{
                minWidth: screenSize >= 576 ? "76px" : "58px",
                // display: "inline-block",
              }}
            >
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "0.6rem" : "0.74rem",
                  fontSize: screenSize >= 992 ? "0.76rem" : screenSize <= 991 && screenSize >= 576 ? "0.76rem" : "0.72rem",
                  fontWeight: "400",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                  letterSpacing: ".05em",
                }}
              >
                {t("app.fund.monthly")}
              </div>
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "1.1rem" : "1.14rem",
                  fontSize: screenSize >= 992 ? "1.3rem" : screenSize <= 991 && screenSize >= 576 ? "1.3rem" : "1.3rem",
                  fontWeight: "400",
                  lineHeight: "1.1",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                  marginTop: screenSize <= 991 ? "3px" : "2px",
                }}
              >
                {numberWithCommas(monthlyReturn.whole) + (monthlyReturn.hasDecimal ? '.' : '')}
                {monthlyReturn.hasDecimal ? (
                <span
                  style={{
                    fontSize: screenSize >= 992 ? ".965rem" : screenSize <= 991 && screenSize >= 576 ? "0.965rem" : ".85rem",
                  }}
                >
                  {monthlyReturn.decimal}
                </span>
                ) : ''}
                <span
                  style={{
                    fontSize: screenSize >= 992 ? "1rem" : screenSize <= 991 && screenSize >= 576 ? "0.8rem" : "0.75rem",
                    color: theme === "dark" ? "rgba(210, 124, 216, .86)" : "rgba(16, 109, 221, .76)",
                    marginLeft: '4px'
                  }}
                >
                  %
                </span>
              </div>
            </div>
        </div>
        <div className='col p-0'>
            <div
              className='text-right-fixed'
              style={{
                minWidth: screenSize >= 576 ? "76px" : "58px",
                // display: "inline-block",
              }}
            >
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "0.6rem" : "0.74rem",
                  fontSize: screenSize >= 992 ? "0.76rem" : screenSize <= 991 && screenSize >= 576 ? "0.76rem" : "0.72rem",
                  fontWeight: "400",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                  letterSpacing: ".05em",
                }}
              >
                {t("app.fund.yearly")}
              </div>
              <div
                className=''
                style={{
                  // fontSize: screenSize <= 991 ? "1.1rem" : "1.14rem",
                  fontSize: screenSize >= 992 ? "1.3rem" : screenSize <= 991 && screenSize >= 576 ? "1.3rem" : "1.3rem",
                  fontWeight: "400",
                  lineHeight: "1.1",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                  marginTop: screenSize <= 991 ? "3px" : "2px",
                }}
              >
                {numberWithCommas(yearlyReturn.whole) + (yearlyReturn.hasDecimal ? '.' : '')}
                {yearlyReturn.hasDecimal ? (
                <span
                  style={{
                    fontSize: screenSize >= 992 ? ".965rem" : screenSize <= 991 && screenSize >= 576 ? "0.965rem" : ".85rem",
                  }}
                >
                  {yearlyReturn.decimal}
                </span>
                ) : ''}
                <span
                  style={{
                    fontSize: screenSize >= 992 ? "1rem" : screenSize <= 991 && screenSize >= 576 ? "0.8rem" : "0.75rem",
                    color: theme === "dark" ? "rgba(210, 124, 216, .86)" : "rgba(16, 109, 221, .76)",
                    marginLeft: '4px'
                  }}
                >
                  %
                </span>
              </div>
            </div>
        </div>
        <div className='col col-md-2 col-lg-2 col-xl-2 p-0 ps-sm-1 pe-sm-2'>
          <div className='row w-100 mx-auto align-items-center gap-md-3 flex-sm-nowrap justify-content-end flex-nowrap'>
            <div className='col-auto col-lg p-0 ps-xl-2 pt-2 text-center text-lg-end'>
              <button
                className='btn btn-primary'
                type='button'
                onClick={() => toggleModal(data, { amount: investAmount, units: unitsWithComma.split(",").join("") })}
                style={{
                  // backgroundColor: 'var(--theme-light-primary)',
                  minWidth: '81px',
                  border: "none",
                  borderRadius: "6px",
                  color: "rgba(255,255,255,.85)",
                  fontSize: screenSize >= 576 ? ".85rem" : ".7rem",
                  fontWeight: "400",
                  padding: screenSize >= 576 ? ".35rem .8rem .4rem" : ".3rem .7rem .35rem",
                }}
              >
                {t("app.fund.purchase")}
                {/* <svg  xmlns="http://www.w3.org/2000/svg"  width={24}  height={24}  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth={2}  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-credit-card-pay"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 19h-6a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5" /><path d="M3 10h18" /><path d="M16 19h6" /><path d="M19 16l3 3l-3 3" /><path d="M7.005 15h.005" /><path d="M11 15h2" /></svg> */}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

FundItem.displayName = 'FundItem';

export default FundItem;
